import PluginDecorator from './PluginDecorator';
import SkyClickPlayer, {SkyClickPlayerEventType} from "../../components/custom/SkyClickPlayer";
import {PlayerComponentHookType} from "../../components/wrappers/PlayerComponentWrapper";
import {
    ResolutionSwitcherEventTypes,
    default as ResolutionSwitcherPluginDecorator
} from "./ResolutionSwitcherPluginDecorator";
import PlaylistNextButton from "../../components/custom/PlaylistNextButton";
import PlaylistPreviousButton from "../../components/custom/PlaylistPreviousButton";
import mergeOptions from "../../utils/wrappers/merge-options";

// load plugin
require('videojs-playlist/dist/videojs-playlist.js');

export type PlaylistEventType =
    'PLAYLIST_ITEM_CHANGE' |  // event alias, playlistitem
    'PLAYLIST_UPDATED' | // custom event, when playlist object is updated (e.g. source property changed of a item, ...)
    'PLAYLIST_ENDED';

export default class PlaylistPluginDecorator extends PluginDecorator{

    static readonly PLUGIN_NAME = 'playlist';

    static bootingStaticApi(){
        super.bootingStaticApi();
        this.hookManagerInstance.add<PlayerComponentHookType>('BEFORE_SETUP', (videoEl:Element, options: any) => {
            return this.processOptions(options);
        });
    }

    static processOptions(options: any) {
        // transform single video (one or multiple qualities) in to playlist
        if (!options.playlist) {
            let playlistItem:any = {
                qualities: <any>[]
            };

            // TODO outsource
            // copy optional properties (poster, tracks) to playlist element
            options.poster && (playlistItem.poster = options.poster);
            options.textTracks && (playlistItem.textTracks = options.textTracks);

            if (!options.qualities) {
                // single video with on quality
                playlistItem.qualities.push(
                    <any>{
                        src: options.src,
                    }
                );
            } else {
                // single video with multiple qualities
                for (let quality of options.qualities) {
                    playlistItem.qualities.push(<any>quality);
                }
            }
            // add single element
            options.playlist = [playlistItem];
        }

        // transform own playlist format to system playlist format
        for (let playlistItem of options.playlist) {
            // take always first quality video - later the multiresolution switcher will load the correct quality

            // define default quality, when not defined
            if(!playlistItem.qualities){
                playlistItem.qualities = [
                    {
                        src: playlistItem.src
                    }
                ]
            }

            let firstVideoQuality = playlistItem.qualities[0].src;
            playlistItem.sources = [{
                src: firstVideoQuality
            }];

            // default values
            !playlistItem.textTracks && (playlistItem.textTracks = []);
        }

        // define default plugin options


        options.plugins.playlist = options.plugins.playlist || {};

        // Don't deactivate next button at end, when related plugin is used
        let playlistOptions = options.plugins.playlist;
        playlistOptions.deactivateNextButtonAtEnd = true;
        if(options.customOptions.plugins && typeof options.customOptions.plugins.related !== 'undefined'){
            playlistOptions.deactivateNextButtonAtEnd = false;
        }

        // extend default configuration with custom configuration
        options.plugins.playlist = mergeOptions({},{
            autoadvance: 0 // auto switch to next playlist item after 0 seconds (documentation: https://github.com/brightcove/videojs-playlist/blob/master/docs/api.md#playerplaylistautoadvancenumber-delay---undefined)
        },options.plugins.playlist);

        return options;
    }

    protected playlist:VideoJSPlaylist;

    protected options:any;

    protected previousButton:PlaylistPreviousButton;
    protected nextButton:PlaylistNextButton;

    protected stateBackup:any = {
        currentPosition:0
    };

    public bootingExecution(){
        // must by initialized on the skyClickPlayer to have the correct context
        this.skyClickPlayer.playlist(<Object[]>this.skyClickPlayer.options_.playlist);

        // reference to playlist instance
        this.playlist = this.skyClickPlayer.playlist;

        this.options = this.skyClickPlayer.getOptions().plugins.playlist;

        // Setup auto switch to next item
        this.playlist.autoadvance(this.skyClickPlayer.getOptions().plugins.playlist.autoadvance);

        this.eventManager.addListener<PlaylistEventType>('PLAYLIST_ITEM_CHANGE', this.onPlaylistChange.bind(this));
        this.eventManager.addListener<ResolutionSwitcherEventTypes>('RESOLUTION_CHANGED', this.onResolutionChange.bind(this));

        this.makeCurrentItemFunctionStable();

        this.initButtons();

        // backup state
        this.backupState(this.getCurrentItem());

        // Trigger by startup 'UPDATED_PLAYLIST', when playlist is initialized
        this.eventManager.trigger<PlaylistEventType>('PLAYLIST_UPDATED', this.getCurrentItem());

        // Init playlist end listener
        this.initPlaylistEndListener();
    }

    /**
     * Restore state, when the player.playlist.currentItem loos index
     * (That you can see, when player.playlist.currentIndex_ is -1)
     *
     * This happen from time to time randomly, because some other events run sometimes before
     * our event listener (this.onResolutionChange) is triggerd.this is the case,
     * When this happen, we have sometimes in the booting process of our player a corrupt playlist index.
     * This function solve now this problem. Always when currentItem Function loose the current play index, we restore it
     */
    protected makeCurrentItemFunctionStable(){
        let originalCurrentItemFunction = this.playlist.currentItem ;
        let playlist = this.playlist;
        // wrap playlist.currentItem function in a custom function, that restore curren play index, when needed.
        this.playlist.currentItem = (index) => {
            let ret = originalCurrentItemFunction.apply(playlist, [index]);
            if(playlist.currentIndex_ === -1){
                this.restoreState();
                // Return restored index, because default return value is wrong
                return playlist.currentIndex_;
            }
            // When current index is not -1, then return received return value
            return ret;
        }
    }

    /**
     * Render playlist navigation buttons, when more than one item exists
     */
    protected initButtons(){
        if(this.getCount()>1){
            // get play button position
            const controlBar = this.skyClickPlayer.getChild('ControlBar');
            const playButtonIx = controlBar.children().indexOf(controlBar.getChild('PlayToggle'))+1;

            // Previous Button
            this.previousButton = controlBar.addChild('PlaylistPreviousButton',{
                clickHandler: this.previous.bind(this)
            },playButtonIx) as PlaylistPreviousButton;

            // Next Button
            this.nextButton = controlBar.addChild('PlaylistNextButton',{
                clickHandler: this.next.bind(this)
            },playButtonIx+1) as PlaylistNextButton;

            this.updateControlButtonsState(this.getCurrentItem());
        }
    }

    protected initPlaylistEndListener(){
        // this.eventManager.trigger<SkyClickPlayerEventType>('ENDED',this.currentSrc());
        this.skyClickPlayer.eventManager.addListener<SkyClickPlayerEventType>('ENDED',() => {
            if(this.getCurrentPosition() == this.getCount()-1){
                this.skyClickPlayer.eventManager.trigger<PlaylistEventType>('PLAYLIST_ENDED',true);
            }
        })
    }

    protected get systemEventAliases(){
        return {
            'playlistitem': 'PLAYLIST_ITEM_CHANGE'
        };
    }

    public getPlaylist(){
        return this.playlist();
    }

    public getCurrentItem():any{
        return this.getPlaylist()[this.getCurrentPosition()];
    }

    public getCurrentPosition():number{
        return <number>this.playlist.currentItem();
    }

    public getCount():number{
        return <number>this.playlist().length;
    }

    public getAutoAdvanceDelay():number{
        return <number>this.playlist.autoadvance_.delay;
    }

    public next(){
        if(this.getCurrentPosition() == this.getCount()-1){
            // playlist end reached and next button is not deactivated (because option "deactivateNextButtonAtEnd" is false), then
            // ...  trigger PlAYLIST_ENDED event, that is used to show related viewer.
            this.skyClickPlayer.eventManager.trigger<PlaylistEventType>('PLAYLIST_ENDED',false);
        }else{
            // next playlist item is available
            this.playlist.next();
        }

    }

    public previous(){
        this.playlist.previous();
    }

    protected onPlaylistChange(event:any,currentItem:any){
        this.backupState(currentItem);
        this.updateControlButtonsState(currentItem);
    };

    protected backupState(currentItem:any){
        // backup current playlist state
        let stateBackup = this.stateBackup;
        stateBackup.currentPosition = this.getPlaylist().indexOf(currentItem);
    }

    /**
     * Update button state dependent on current playing position
     */
    protected updateControlButtonsState(currentItem:any){
        const currentPlayingPosition = this.getPlaylist().indexOf(currentItem);

        // Disable previous button at first playlist item
        if(currentPlayingPosition === 0){
            this.previousButton.addClass('vjs-disabled')
        } else{
            this.previousButton.removeClass('vjs-disabled');
        }

        // Disable next button at last playlist item
        // (When option "deactivateNextButtonAtEnd" is true, see more in PlaylistPluginDecorator.processOptions function)
        if(currentPlayingPosition === (this.getCount()-1)  && this.options.deactivateNextButtonAtEnd) {
            this.nextButton.addClass('vjs-disabled')
        }else{
            this.nextButton.removeClass('vjs-disabled');
        }
    }

    protected onResolutionChange(){
        this.restoreState();
        this.updateResolutionInCurrentItem();
    };

    /**
     * Triggered before resolution switcher has updated sources
     */
    protected restoreState(){
        // restore playlist state
        let stateBackup = this.stateBackup;
        // ToDo: try to not restore current index, because we can update the resolution in the source (see this.updateResolutionInCurrentItem)
        this.playlist.currentIndex_ = stateBackup.currentPosition;
    }

    /**
     * Update the selected resolution in the current playlist item source,
     * that this.getCurrentItem (behind playlist.currentItem), can search source name
     */
    protected updateResolutionInCurrentItem(){
        // we use private index, because the public getter destroy current index, in case of incorrect source
        const currentIndex = (<any>this.playlist).currentIndex_;
        const currentResolution = this.resolutionSwitcherDecorator.getCurrentResolution();
        // only update, when current index and currentResolution is defined
        if(currentIndex !== -1 && currentResolution.sources){
            this.getPlaylist()[currentIndex].sources[0].src = currentResolution.sources[0].src;
            // Trigger event, because playlist object is updated
            this.eventManager.trigger<PlaylistEventType>('PLAYLIST_UPDATED', this.getPlaylist()[currentIndex]);
        }
    }

}


// WEBPACK FOOTER //
// ./src/app/plugins/decorators/PlaylistPluginDecorator.ts